import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { formatVideoClock } from '@/lib/videoFrame';
import { useEffect, useRef, useState } from 'react';

type VideoFrameCaptureModalProps = {
  open: boolean;
  videoSrc: string | null;
  title?: string;
  description?: string;
  confirmLabel?: string;
  onClose: () => void;
  /** Recebe o object URL do JPEG capturado (caller deve revogar). */
  onCapture: (objectUrl: string, file: File) => void | Promise<void>;
  loading?: boolean;
};

/**
 * Permite posicionar o vídeo e capturar um frame para usar como capa (crop em seguida).
 */
export function VideoFrameCaptureModal({
  open,
  videoSrc,
  title = 'Capturar capa do vídeo',
  description = 'Arraste a barra para escolher o momento e capture o frame.',
  confirmLabel = 'Usar este frame',
  onClose,
  onCapture,
  loading = false,
}: VideoFrameCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    if (!open) {
      setDuration(0);
      setCurrentTime(0);
      setReady(false);
      setError('');
      setCapturing(false);
    }
  }, [open, videoSrc]);

  function handleLoadedMetadata() {
    const video = videoRef.current;
    if (!video) return;
    const d = Number.isFinite(video.duration) ? video.duration : 0;
    setDuration(d);
    const initial = d > 0 ? Math.min(1, d * 0.1) : 0;
    video.currentTime = initial;
    setCurrentTime(initial);
    setReady(true);
  }

  function handleSeek(value: number) {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = value;
    setCurrentTime(value);
  }

  async function handleCapture() {
    const video = videoRef.current;
    if (!video || !ready) return;

    setError('');
    setCapturing(true);
    try {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) {
        throw new Error('Vídeo ainda não está pronto para captura.');
      }

      const maxWidth = 1280;
      const scale = vw > maxWidth ? maxWidth / vw : 1;
      const width = Math.round(vw * scale);
      const height = Math.round(vh * scale);

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Não foi possível preparar a captura.');
      }
      ctx.drawImage(video, 0, 0, width, height);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Falha ao gerar a imagem.'))),
          'image/jpeg',
          0.92,
        );
      });

      const file = new File([blob], 'animal-cover-from-video.jpg', { type: 'image/jpeg' });
      const objectUrl = URL.createObjectURL(blob);
      await onCapture(objectUrl, file);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao capturar o frame.');
    } finally {
      setCapturing(false);
    }
  }

  const busy = loading || capturing;

  return (
    <Modal
      open={open}
      onClose={() => !busy && onClose()}
      title={title}
      size="lg"
      footer={
        <>
          <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" loading={busy} disabled={!ready || busy} onClick={handleCapture}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-manejai-bark/70">{description}</p>

        <div className="overflow-hidden rounded-xl bg-black">
          {videoSrc ? (
            <video
              ref={videoRef}
              src={videoSrc}
              crossOrigin="anonymous"
              className="max-h-[50vh] w-full"
              muted
              playsInline
              preload="metadata"
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={() => {
                const v = videoRef.current;
                if (v) setCurrentTime(v.currentTime);
              }}
              onError={() => setError('Não foi possível carregar o vídeo para captura.')}
            />
          ) : null}
        </div>

        <div className="space-y-2">
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.05}
            value={Math.min(currentTime, duration || 0)}
            disabled={!ready || busy || duration <= 0}
            onChange={(e) => handleSeek(Number(e.target.value))}
            className="w-full accent-manejai-forest"
            aria-label="Posição no vídeo"
          />
          <div className="flex justify-between text-xs text-manejai-bark/60">
            <span>{formatVideoClock(currentTime)}</span>
            <span>{formatVideoClock(duration)}</span>
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
